// S-3050: Hook for fetching swing runs with Realtime updates and polling fallback

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { SwingRun } from '@/types/swing-run';
import { RealtimeChannel } from '@supabase/supabase-js';

interface UseSwingRunsFeedOptions {
  pollingInterval?: number; // default 20000 (20s)
  enableRealtime?: boolean; // default true
  limit?: number; // max runs to fetch
}

interface UseSwingRunsFeedResult {
  runs: SwingRun[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const DEFAULT_POLLING_INTERVAL = 20000; // 20 seconds
const DEFAULT_LIMIT = 100;

export function useSwingRunsFeed(options?: UseSwingRunsFeedOptions): UseSwingRunsFeedResult {
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    enableRealtime = true,
    limit = DEFAULT_LIMIT,
  } = options ?? {};

  const [runs, setRuns] = useState<SwingRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load runs from database
  const loadRuns = useCallback(async () => {
    try {
      console.log('[useSwingRunsFeed] Loading runs...');

      const { data, error: loadError } = await supabase
        .from('swing_runs')
        .select(
          'id, user_id, status, cf_stream_uid, cf_playback_url, original_filename, created_at, updated_at, schema_version, legacy_video_id'
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (loadError) {
        console.error('[useSwingRunsFeed] Supabase error:', {
          message: loadError.message,
          details: loadError.details,
          hint: loadError.hint,
          code: loadError.code
        });
        throw loadError;
      }

      console.log(`[useSwingRunsFeed] Loaded ${data?.length || 0} runs`);
      setRuns(data || []);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('[useSwingRunsFeed] Load error:', errorMessage);
      setError(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  // Handle Realtime INSERT event
  const handleInsert = useCallback((payload: any) => {
    console.log('[useSwingRunsFeed] Realtime INSERT:', payload.new.id);
    const newRun = payload.new as SwingRun;

    setRuns((prev) => {
      // Add to beginning of list
      return [newRun, ...prev];
    });
  }, []);

  // Handle Realtime UPDATE event
  const handleUpdate = useCallback((payload: any) => {
    console.log('[useSwingRunsFeed] Realtime UPDATE:', payload.new.id);
    const updatedRun = payload.new as SwingRun;

    setRuns((prev) => {
      // Replace existing run by id
      return prev.map((run) => (run.id === updatedRun.id ? updatedRun : run));
    });
  }, []);

  // Handle Realtime DELETE event
  const handleDelete = useCallback((payload: any) => {
    console.log('[useSwingRunsFeed] Realtime DELETE:', payload.old.id);
    const deletedId = payload.old.id;

    setRuns((prev) => prev.filter((run) => run.id !== deletedId));
  }, []);

  // Set up Realtime subscription
  useEffect(() => {
    if (!enableRealtime) {
      console.log('[useSwingRunsFeed] Realtime disabled');
      return;
    }

    console.log('[useSwingRunsFeed] Setting up Realtime subscription...');

    const channel = supabase
      .channel('swing_runs_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'swing_runs',
          // RLS automatically filters to current user
        },
        handleInsert
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'swing_runs',
        },
        handleUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'swing_runs',
        },
        handleDelete
      )
      .subscribe((status) => {
        console.log('[useSwingRunsFeed] Realtime status:', status);
      });

    channelRef.current = channel;

    return () => {
      console.log('[useSwingRunsFeed] Cleaning up Realtime subscription');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enableRealtime, handleInsert, handleUpdate, handleDelete]);

  // Set up polling fallback
  useEffect(() => {
    if (pollingInterval <= 0) {
      console.log('[useSwingRunsFeed] Polling disabled');
      return;
    }

    console.log(`[useSwingRunsFeed] Setting up polling fallback (${pollingInterval}ms)...`);

    const interval = setInterval(() => {
      console.log('[useSwingRunsFeed] Polling fallback triggered');
      loadRuns();
    }, pollingInterval);

    pollingIntervalRef.current = interval;

    return () => {
      console.log('[useSwingRunsFeed] Cleaning up polling interval');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [pollingInterval, loadRuns]);

  // Initial load
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  return {
    runs,
    isLoading,
    error,
    refresh: loadRuns,
  };
}

